import { toError } from "@clipboard-health/util-ts";
import { RootPaths } from "@src/appV2/App/paths";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { type ReactElement } from "react";
import { useHistory } from "react-router-dom";

import { PageWrapper } from "../../components/PageWrapper";
import { AddRolesErrorState } from "../../Roles/AddRoles/ErrorState";
import { AddRolesFooter } from "../../Roles/AddRoles/Footer";
import { AddRolesLoadingState } from "../../Roles/AddRoles/LoadingState";
import { AddRolePageContent } from "../../Roles/AddRoles/PageContent";
import { useAddRolesData } from "../../Roles/AddRoles/useAddRolesData";
import { useCreateShoppableQualification } from "../../Roles/api/useCreateShoppableQualification";
import { useDeleteShoppableQualification } from "../../Roles/api/useDeleteShoppableQualification";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { useUpdateOnboardingWorker } from "../api/useUpdateOnboardingWorker";
import { OnboardingHeader } from "../Header";

interface OnboardingAddRolesPageProps {
  nextStagePath: (isPermanentPlacement: boolean, isSurfaceShiftsEnabled: boolean) => string;
}

/**
 * @deprecated. We are working on deprecating this page. Once we have have new signing up flow, this page will be removed.
 *
 * New roles page: src/appV2/redesign/Onboarding/OnboardingFlowContainer/RolesStep/Container.tsx
 */
export function OnboardingAddRolesPage(props: OnboardingAddRolesPageProps): ReactElement {
  const { nextStagePath } = props;

  const {
    allQualifications,
    qualificationsByCategories,
    qualificationsAreLoading,
    qualificationsAreLoaded,
    qualificationsAreError,
    autoApprovedQualifications,
    workerId,
    workerQualificationIds,
    scrollContainerRef,
    shoppableQualifications,
  } = useAddRolesData();

  const { showErrorToast } = useToast();
  const history = useHistory();

  const isKeyboardOpen = useIsKeyboardOpen({ keyboardOpenEvent: "keyboardWillShow" });

  const { mutateAsync: createShoppableQualification, isLoading: isCreatingShoppableQualification } =
    useCreateShoppableQualification();

  const { mutateAsync: updateOnboardingWorker, isLoading: isUpdatingOnboardingWorker } =
    useUpdateOnboardingWorker();

  const { mutateAsync: deleteShoppableQualification, isLoading: isDeletingShoppableQualification } =
    useDeleteShoppableQualification();

  useLogEffect(APP_V2_USER_EVENTS.ONBOARDING_ADD_ROLES_PAGE_VIEWED);

  if (qualificationsAreError) {
    return <AddRolesErrorState />;
  }

  return (
    <PageWrapper variant="tertiary">
      <OnboardingHeader
        title="What type of roles are you interested in?"
        fallbackGoBackPath={`${RootPaths.HOME}/onboarding`}
        compact={isKeyboardOpen}
      />

      <PageContent>
        {qualificationsAreLoading && <AddRolesLoadingState />}

        {qualificationsAreLoaded && (
          <AddRolePageContent
            ref={scrollContainerRef}
            qualificationsByCategories={qualificationsByCategories}
            autoApprovedQualifications={autoApprovedQualifications}
            workerQualificationIds={workerQualificationIds}
            onCreateShoppableQualification={async (qualificationId) => {
              const qualification = allQualifications.find(
                (qualification) => qualification.id === qualificationId
              );

              if (!qualification) {
                return;
              }

              try {
                await createShoppableQualification({
                  workerId,
                  qualificationId,
                });

                logEvent(APP_V2_USER_EVENTS.ROLE_ADDED, {
                  qualificationId,
                  workerId,
                  isOnboarding: true,
                  screen: "SIGNUP",
                });
              } catch (maybeError) {
                const error = toError(maybeError);

                logError(APP_V2_APP_EVENTS.ADD_ROLE_FAILURE, {
                  metadata: {
                    qualificationId,
                    workerId,
                    isOnboarding: true,
                    screen: "SIGNUP",
                  },
                  error,
                });
              }
            }}
          />
        )}
      </PageContent>

      <AddRolesFooter
        addedQualificationIds={workerQualificationIds}
        qualifications={allQualifications}
        shoppableQualifications={shoppableQualifications}
        isVisible={isKeyboardOpen}
        isLoadingContinueButton={isUpdatingOnboardingWorker}
        canContinue={
          workerQualificationIds.length > 0 &&
          !isCreatingShoppableQualification &&
          !isDeletingShoppableQualification
        }
        onDeleteRole={async (shoppableQualificationId, qualificationId) => {
          await deleteShoppableQualification({
            workerId,
            shoppableQualificationId,
          });

          logEvent(APP_V2_USER_EVENTS.ROLE_REMOVED, {
            qualificationId,
            workerId,
            isOnboarding: true,
            screen: "SIGNUP",
          });
        }}
        onDeleteRoleError={async (qualificationId, error) => {
          showErrorToast("Something went wrong while removing the role. Please try again.");

          logError(APP_V2_APP_EVENTS.REMOVE_ROLE_FAILURE, {
            metadata: {
              qualificationId,
              workerId,
              isOnboarding: true,
              screen: "SIGNUP",
            },
            error,
          });
        }}
        onDone={async () => {
          await updateOnboardingWorker({
            stage: "updateStageOnly",
            newStage: "agentLicense",
          });

          // passing false for isPermanentPlacement because it is supposed to be killed
          // passing true for isSurfaceShiftsEnabled because we know that surface shifts are enabled as this step renders
          history.push(nextStagePath(false, true));
        }}
      />
    </PageWrapper>
  );
}
